// Pure coercers for session-payload shapes. Tolerate older blob layouts
// so legacy sessions keep loading after schema changes. No React, no
// module-level state — tested by sessionCoercers.test.ts.

import type { NewModel, Vsm, VsmProcess, VsmStep } from './flowModel';
import type {
  SessionData,
  DiscoveryFeature,
  DiscoveryFeaturesPayload,
} from '../hooks/sessionTypes';

// Coerce the discoveryFeatures field from the session blob to the
// shape the panel expects, defaulting status from the meta table.
export function coerceDiscoveryFeatures(
  raw: SessionData['discoveryFeatures'],
  status: SessionData['featuresStatus'],
): DiscoveryFeaturesPayload {
  return {
    features: (raw?.features as DiscoveryFeature[] | undefined) || [],
    extractedAt: raw?.extractedAt ?? null,
    statementCountAtExtraction: raw?.statementCountAtExtraction ?? null,
    warnings: raw?.warnings || [],
    status: (status === 'pending' ? 'pending' : 'idle'),
  };
}

// Coerce a stored newModel blob to the current NewModel shape. Legacy sessions
// were saved as { flow: string[], openQuestions: [{priority, text}], nextQuestion }
// before the markdown migration; tolerate that on read so old sessions still load.
export function coerceNewModel(raw: unknown): NewModel {
  const empty: NewModel = { flowText: '', openQuestions: '', nextQuestion: '', raw: '', frozenBoundary: 0 };
  if (!raw || typeof raw !== 'object') return empty;
  const r = raw as Record<string, unknown>;
  const flowText =
    typeof r.flowText === 'string'
      ? r.flowText
      : Array.isArray(r.flow)
        ? (r.flow as string[]).join('\n')
        : '';
  const openQuestions =
    typeof r.openQuestions === 'string'
      ? r.openQuestions
      : Array.isArray(r.openQuestions)
        ? (r.openQuestions as Array<{ priority?: number; text?: string }>)
            .map((q) => `- [P${q.priority ?? '?'}] ${q.text ?? ''}`)
            .join('\n')
        : '';
  const vsm = coerceVsm(r.vsm);
  return {
    flowText,
    openQuestions,
    nextQuestion: typeof r.nextQuestion === 'string' ? r.nextQuestion : '',
    raw: typeof r.raw === 'string' ? r.raw : '',
    frozenBoundary: typeof r.frozenBoundary === 'number' ? r.frozenBoundary : 0,
    ...(vsm ? { vsm } : {}),
    // lastTurn is server-shaped; passed through opaquely. Pre-migration
    // sessions don't have it — the Studio panel handles undefined.
    lastTurn: (r.lastTurn && typeof r.lastTurn === 'object')
      ? (r.lastTurn as NewModel['lastTurn'])
      : undefined,
  };
}

// Coerce a stored vsm blob. Returns undefined when the field is absent
// (legacy sessions) so the spread above omits it entirely — the canvas
// handles missing vsm as "no processes captured yet".
function coerceVsm(raw: unknown): Vsm | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const r = raw as Record<string, unknown>;
  if (!Array.isArray(r.processes)) return { processes: [] };
  const processes: VsmProcess[] = [];
  for (const p of r.processes) {
    if (!p || typeof p !== 'object') continue;
    const pr = p as Record<string, unknown>;
    const id = typeof pr.id === 'string' ? pr.id : null;
    const label = typeof pr.label === 'string' ? pr.label : null;
    if (!id || !label) continue;
    const steps: VsmStep[] = [];
    if (Array.isArray(pr.steps)) {
      for (const s of pr.steps) {
        if (!s || typeof s !== 'object') continue;
        const sr = s as Record<string, unknown>;
        const statementId = typeof sr.statementId === 'number' ? sr.statementId : null;
        if (statementId === null) continue;
        steps.push({
          statementId,
          ct: typeof sr.ct === 'string' ? sr.ct : '0',
          wt: typeof sr.wt === 'string' ? sr.wt : '0',
          ca: typeof sr.ca === 'number' ? sr.ca : 100,
        });
      }
    }
    processes.push({ id, label, steps });
  }
  return { processes };
}
